import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '../../material.module';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PatientFormsComponent } from '../patient-forms/patient-forms.component';
import { FormSelectorComponent } from '../form-selector/form-selector.component';
import { FormConfigService } from '../forms-config.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppStateService } from '../../app-state.service';
import { ProfileComponent } from '../profile/profile.component';
import { ActivatedRoute, Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { NotificationsComponent } from '../notifications/notifications.component';
import { PatientDashboardMainComponent } from '../patient-dashboard-main/patient-dashboard-main.component';
import { AppEventService } from '../../app-event-service';
import { PatientProfile } from '../patient.model';
import { ClinicInformation } from '../clinic.model';
import { forkJoin } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ClinicInfoComponent } from "../clinic-info/clinic-info.component";
import { ThemeSwitcherComponent } from '../../core/components/theme-switcher/theme-switcher.component';
import { DeeplinkService } from '../../deeplink.service';
import { MatDialog } from '@angular/material/dialog';
import { RegistrationPromptComponent } from '../registration-prompt/registration-prompt.component';
import { ConsentPromptComponent } from '../consent-prompt/consent-prompt.component';
@Component({
  selector: 'app-patient-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    MatTooltipModule,
    PatientFormsComponent,
    FormSelectorComponent,
    ProfileComponent,
    NotificationsComponent,
    PatientDashboardMainComponent,
    ClinicInfoComponent,
    ThemeSwitcherComponent
],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './patient-dashboard.component.html',
  styleUrls: ['./patient-dashboard.component.scss']
})
export class PatientDashboardComponent implements OnInit, OnDestroy {
  patientId: string;
  clientId: string = '';
  searchForm: FormGroup;
  notifications: string[] = ['You currently have no notifications.'];
  patient: PatientProfile = {} as PatientProfile;
  showSearch = false;
  loading = false;
  tenantId: string = '';
  isEmbedded: boolean = false;
  currentRoute: string = '';
  
  // Forms reminder properties
  showFormsReminder: boolean = false;
  showReminderPopup: boolean = false;
  pendingForms: string[] = [];
  private reminderCheckInterval: any;

  formTemplates: any[] = [];
  filteredFormTemplates: any[] = [];
  eventService = inject(AppEventService);
  clinicInfo: ClinicInformation = {} as ClinicInformation;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private appStateService: AppStateService,
    private formConfigService: FormConfigService,
    private snackBar: MatSnackBar,
    private deeplinkService: DeeplinkService,
    private dialog: MatDialog
  ) {
    this.clientId = this.appStateService.getState().clientId || '';
    this.patientId = this.appStateService.getState().patientId || sessionStorage.getItem('patientId') || '';
    this.tenantId = this.appStateService.getState().tenantId || '';
    this.searchForm = this.fb.group({
      search: ['']
    });
  }

  ngOnInit(): void {
    
    // Double-check and update patientId if it's still empty
    if (!this.patientId) {
      this.patientId = this.appStateService.getState().patientId || sessionStorage.getItem('patientId') || '';
    }
    
    // Check if running inside an iframe
    this.isEmbedded = window.self !== window.top;
    this.loading = true;

    // Initialize current route and subscribe to router events for active state highlighting
    this.currentRoute = this.router.url;
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.currentRoute = event.url;
        }
      });
      
    // Subscribe to registration completed event to show consent form
    this.appStateService.registrationCompleted$.subscribe(completed => {
      if (completed && this.appStateService.getState().showConsentAfterRegistration) {
        // Clear the flag first
        this.appStateService.setState({ showConsentAfterRegistration: false });
        // Show consent prompt after registration is complete
        setTimeout(() => {
          this.showConsentPrompt();
        }, 500);
      }
    });

    // Validate required IDs before making API calls
    if (!this.clientId) {
      this.loading = false;
      return;
    }
    
    if (!this.patientId) {
      this.loading = false;
      return;
    }
    
    forkJoin([
      this.formConfigService.fetchClientInfo(this.clientId),
      this.formConfigService.fetchPatient(this.clientId, this.patientId)
    ]).subscribe({
      next: ([clinicInfo, patient]) => {
        this.loading = false;
        this.clinicInfo = clinicInfo;
        this.patient = patient;
        this.appStateService.setState({ patient: this.patient, clinicInfo: this.clinicInfo });
        
        // Check for pending forms initially
        this.checkPendingForms();
        
        // Start polling for pending forms changes every second
        this.reminderCheckInterval = setInterval(() => {
          this.checkPendingForms();
        }, 1000);

        const isNewRegistration = this.patient.isNewPatient?.toLowerCase() === 'yes' && !this.patient.isRegistered;

        // Check for deep link context first (highest priority)
        const deeplinkContext = this.deeplinkService.getDeeplinkContext();
        if (deeplinkContext && deeplinkContext.url) {
          this.router.navigateByUrl(deeplinkContext.url);
          return;
        }

        const redirectUrl = this.appStateService.getState().redirectUrl;
        const appointmentData = this.appStateService.getState().appointmentData;
        const appointmentBooked = this.appStateService.getState().appointmentBooked;
        
        // Check if we just booked an appointment and need to show registration popup
        // Only show popup if appointment was just booked
        if (appointmentBooked) {
          // Clear the flag immediately to prevent re-triggering
          this.appStateService.setState({ appointmentBooked: false });

          // Check if this patient needs to fill any forms
          // Show registration prompt if patient is not registered (regardless of isNewPatient flag)
          const needsRegistration = !this.patient.isRegistered;
          const needsConsent = !this.patient.hasCompletedConsent;

          if (needsRegistration || needsConsent) {
            // Wait for view to settle before showing dialog
            setTimeout(() => {
              if (needsRegistration) {
                // Registration prompt will chain to consent prompt if user skips
                this.showRegistrationPrompt();
              } else if (needsConsent) {
                // Only show consent prompt if registration is already done
                this.showConsentPrompt();
              }
            }, 500);
          }
          return;
        }
        
        // DO NOT automatically redirect new patients to registration form
        // They should only see it after booking an appointment
        
        if (redirectUrl && redirectUrl !== '') {
          this.appStateService.setState({ redirectUrl: '' });
          // Map account_statement and account_details to account-history
          let targetRoute = redirectUrl;
          if (redirectUrl === 'account_statement' || redirectUrl === 'account_details') {
            targetRoute = 'account-history';
          }
          // Map insurance or insurance_manage to insurance/manage route
          if (redirectUrl === 'insurance' || redirectUrl === 'insurance_manage') {
            targetRoute = 'insurance/manage';
          }
          this.router.navigate([targetRoute], { relativeTo: this.route });
          return;
        }

        // Check if we're already on a specific child route (e.g., /dashboard/appointment/new)
        // If so, don't auto-navigate to main - let the intended route load
        const currentUrl = this.router.url;
        const isOnChildRoute = currentUrl.includes('/dashboard/') && !currentUrl.endsWith('/dashboard');

        if (!isOnChildRoute) {
          // Always navigate to main dashboard, regardless of registration status
          // New patients will see registration popup only after booking appointment
          this.router.navigate(['main'], { relativeTo: this.route });
        }
      },
      error: () => {
        this.showError('Error fetching clinic information.');
      }
    });
  }


  toggleSearch(): void {
    this.showSearch = !this.showSearch;
  }

  goHome() {
    this.router.navigate(['/dashboard', 'main']);
  }

  navigateToForm(template: any): void {
    const route = this.getRouteForTemplate(template);
    if (route) {
      this.router.navigate([route]);
    } else {
      this.showError('Form type not recognized.');
    }
  }

  navigateToFamily(): void {
    this.router.navigate(['/dashboard/patients-list']);
  }

  navigateToNotifications(): void {
    this.router.navigate(['/dashboard/notifications']);
  }

  navigateToDocuments(): void {
    this.router.navigate(['/dashboard/documents']);
  }

 

  navigateToProfile(): void {
    this.router.navigate(['/dashboard/profile-manage']);
  }

  closeEmbeddedPortal(): void {
    // Send message to parent window to close the iframe
    if (this.isEmbedded && window.parent) {
      window.parent.postMessage({ action: 'closePatientPortal' }, '*');
    }
  }

  getRouteForTemplate(template: any): string | null {
    switch (template.form_id) {
      case 'new_patient_registration':
        return '/registration';
      case 'dental_implant_consent':
      case 'informed_consent_cementation':
      case 'informed_consent_biphosphonates':
      case 'informed_acceptance_dentures':
      case 'informed_refusal_xrays':
      case 'informed_consent_general_dentistry':
      case 'informed_consent_papoose':
      case 'informed_consent_pulpotomy':
      case 'informed_consent_root_canal':
      case 'informed_consent_steel_crown':
      case 'informed_refusal_fmd':
      case 'informed_consent_nitrous_oxide':
        return '/consent';
      default:
        return null;
    }
  }

  navigateToAddress(): void {
    this.router.navigate(['/dashboard/address']);
  }

  navigateToForms(): void {
    this.router.navigate(['/dashboard/all-forms']);
  }

  getInitials(title: string): string {
    const words = title.split(' ');
    return words.length > 1
      ? (words[0][0] + words[1][0]).toUpperCase()
      : words[0][0].toUpperCase();
  }

  showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
    });
  }

  navigateToClinic(): void {
    if (!this.clinicInfo.address) {
      this.showError('Clinic address is not available.');
      return;
    }
    const address = this.clinicInfo.address;
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
  }

  // Methods to check if navigation items are active
  isHomeActive(): boolean {
    return this.currentRoute === '/dashboard/main' || this.currentRoute === '/dashboard';
  }

  isFamilyActive(): boolean {
    return this.currentRoute.includes('/dashboard/patients-list');
  }

  isProfileActive(): boolean {
    return this.currentRoute.includes('/dashboard/profile-manage');
  }

  isFormsActive(): boolean {
    return this.currentRoute.includes('/dashboard/all-forms');
  }

  isDocumentsActive(): boolean {
    return this.currentRoute.includes('/dashboard/documents');
  }

  isNotificationsActive(): boolean {
    return this.currentRoute.includes('/dashboard/notifications');
  }

  private showRegistrationPrompt(): void {
    const dialogRef = this.dialog.open(RegistrationPromptComponent, {
      width: '500px',
      disableClose: false,
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'fill') {
        // User wants to fill registration form
        // Store a flag to show consent form after registration
        this.appStateService.setState({ showConsentAfterRegistration: true });
        this.router.navigate(['forms/new_patient_registration'], { relativeTo: this.route });
      } else {
        // User skipped registration - add to pending forms
        this.addPendingForm('registration');
        // Show consent form prompt
        this.showConsentPrompt();
      }
    });
  }

  private showConsentPrompt(): void {
    const dialogRef = this.dialog.open(ConsentPromptComponent, {
      width: '500px',
      disableClose: false,
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'fill') {
        // User wants to fill consent form
        this.router.navigate(['consent/informed_consent_general_dentistry'], { relativeTo: this.route });
      } else {
        // User skipped consent - add to pending forms
        this.addPendingForm('consent');
      }
      // Clear appointment data after the entire flow
      this.appStateService.setState({ appointmentData: null });
    });
  }

  // Forms reminder methods
  toggleReminderPopup(): void {
    this.showReminderPopup = !this.showReminderPopup;
  }
  
  navigateToPendingForm(formType: string): void {
    this.showReminderPopup = false;
    
    if (formType === 'registration') {
      this.router.navigate(['/dashboard/forms/new_patient_registration']);
    } else if (formType === 'consent') {
      this.router.navigate(['/dashboard/consent/informed_consent_general_dentistry']);
    }
  }
  
  checkPendingForms(): void {
    if (!this.patientId) return;
    
    const pendingFormsKey = `pending_forms_${this.patientId}`;
    const saved = localStorage.getItem(pendingFormsKey);
    
    if (saved) {
      this.pendingForms = JSON.parse(saved);
      
      // Clean up pending forms if patient has already completed them
      if (this.patient) {
        let hasChanges = false;
        
        // Remove registration if patient is already registered
        if (this.patient.isRegistered && this.pendingForms.includes('registration')) {
          this.pendingForms = this.pendingForms.filter(f => f !== 'registration');
          hasChanges = true;
        }
        
        // Remove consent if patient has already completed consent
        if (this.patient.hasCompletedConsent && this.pendingForms.includes('consent')) {
          this.pendingForms = this.pendingForms.filter(f => f !== 'consent');
          hasChanges = true;
        }
        
        // Save cleaned up list if changes were made
        if (hasChanges) {
          this.savePendingForms();
        }
      }
      
      this.showFormsReminder = this.pendingForms.length > 0;
    } else {
      this.pendingForms = [];
      this.showFormsReminder = false;
    }
  }
  
  savePendingForms(): void {
    if (!this.patientId) return;
    
    const pendingFormsKey = `pending_forms_${this.patientId}`;
    if (this.pendingForms.length > 0) {
      localStorage.setItem(pendingFormsKey, JSON.stringify(this.pendingForms));
    } else {
      localStorage.removeItem(pendingFormsKey);
    }
  }
  
  addPendingForm(formType: string): void {
    if (!this.pendingForms.includes(formType)) {
      this.pendingForms.push(formType);
      this.savePendingForms();
      this.showFormsReminder = true;
    }
  }
  
  removePendingForm(formType: string): void {
    this.pendingForms = this.pendingForms.filter(f => f !== formType);
    this.savePendingForms();
    this.showFormsReminder = this.pendingForms.length > 0;
  }
  
  ngOnDestroy(): void {
    // Clear the polling interval
    if (this.reminderCheckInterval) {
      clearInterval(this.reminderCheckInterval);
    }
  }
}
